// src/pages/FamilyDashboard.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bar, Line } from "react-chartjs-2";
import "chart.js/auto";

import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { getFirestore, doc, onSnapshot, setDoc } from "firebase/firestore";
import { useMediaQuery, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button } from "@mui/material";

import { ensureUserDoc } from "../firebase";
import VoiceRecorder from "../components/VoiceRecorder";
import "../App.css";

/* ----------------------------------
   Stripe Add-on price IDs (SET THESE)
----------------------------------- */
const ADDON_PRICE_IDS = {
  extra20:  "price_1SkuoYH9XJICOtRvbyvgyLdw", // ⬅️ replace with your Stripe price ID
  extra100: "price_1SkuowH9XJICOtRv6w4km2TJ", // ⬅️ replace with your Stripe price ID 
};

/* ---------------- Helpers ---------------- */
function asDateMaybe(v) { return dateFromAny(v); }

function formatDate(d) {
  if (!d) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
function defaultCapFromPackageKey(k) {
  const key = String(k || "").toLowerCase();
  if (key.includes("family_lite")) return 80;
  if (key.includes("family")) return 150;
  if (key.includes("personal")) return 50;
  return 0;
}
function normalizeBilling(interval) {
  const iv = String(interval || "").toLowerCase();
  if (iv === "year" || iv === "annual" || iv === "yearly") return "annual";
  if (iv === "month" || iv === "monthly") return "monthly";
  return "—";
}
function startOfCalendarMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
}
function endOfCalendarMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 1, 0, 0, 0, 0); // exclusive end
}
function inRange(dateStrOrObj, start, endExclusive) {
  if (!dateStrOrObj) return false;
  const d = typeof dateStrOrObj === "string" ? new Date(dateStrOrObj) : asDateMaybe(dateStrOrObj);
  if (!d) return false;
  if (start && d < start) return false;
  if (endExclusive && d >= endExclusive) return false;
  return true;
}

/** Extract billing period from user doc (Stripe-based). Fallback to calendar month if missing. */
function getBillingWindow(u) {
  const sub = u?.subscription || null;
  const start = asDateMaybe(sub?.currentPeriodStart);
  const end = asDateMaybe(sub?.currentPeriodEnd);
  if (start && end) return { start, end };

  // fallback: calendar month
  const now = new Date();
  return { start: startOfCalendarMonth(now), end: endOfCalendarMonth(now) };
}

/** Compute the CAP (included stories) from entitlements/legacy/packageKey */
function resolveCap(u) {
  const sub = u?.subscription || null;
  const ent = u?.entitlements || {};
  const legacyPlan = typeof u?.plan === "object" ? u.plan : null;

  let cap =
    Number(ent?.storyQuotaPerMonth ?? (legacyPlan?.capStories != null ? legacyPlan.capStories : 0)) || 0;
  if (!cap && sub?.packageKey) cap = defaultCapFromPackageKey(sub.packageKey);
  return cap;
}

function sumStoriesInWindow(familyProfiles, start, endExclusive) {
  let total = 0;

  for (const p of familyProfiles) {
    // ✅ All-time: use the legacy counter (this is what fixed your sidebar)
    if (!start && !endExclusive) {
      total += Number(p?.storiesGenerated || 0);
      continue;
    }

    // ✅ Range mode: restore the old behaviour:
    // count storiesGenerated if present, otherwise count the entry as 1
    const arr = Array.isArray(p?.readingActivity) ? p.readingActivity : [];
    for (const e of arr) {
      const when = e?.date || e?.ts || e?.time || null;
      if (inRange(when, start, endExclusive)) {
        const n = Number(e?.storiesGenerated || 0);
        total += n > 0 ? n : 1; // ✅ this is the missing piece
      }
    }
  }

  return total;
}


/** Add-ons remaining (non-expired) */
function remainingAddons(u) {
  const nowMs = Date.now();
  const addonsRaw = Array.isArray(u?.addons) ? u.addons : [];
  const remaining = addonsRaw
    .filter((a) => {
      const expMs = msFromAny(a?.expiresAt);
      // keep if no expiry, or expiry is in the future
      return expMs == null || expMs > nowMs;
    })
    .reduce((sum, a) => sum + Number(a?.remaining || 0), 0);
  return remaining; // <-- IMPORTANT: return the computed value
}

function msFromAny(v) {
  if (!v) return null;
  if (typeof v === "number") return v > 1e12 ? v : v * 1000; // seconds → ms
  if (v?.toMillis) { try { return v.toMillis(); } catch {} } // Firestore Timestamp
  if (v?._seconds != null) return v._seconds * 1000;         // admin SDK shape (rare on client)
  const d = new Date(v);
  const ms = d.getTime();
  return Number.isFinite(ms) ? ms : null;
}
function dateFromAny(v) {
  const ms = msFromAny(v);
  return ms == null ? null : new Date(ms);
}

/* ---------------- Component ---------------- */
const FamilyDashboard = ({ overrideUid = null, adminView = false }) => {
  const auth = getAuth();
  const db = getFirestore();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:768px)");

  // 🔹 NEW: remember that this is the last dashboard visited
  useEffect(() => {
    localStorage.setItem("lastDashboard", "/family-dashboard");
  }, []);

  const [userDoc, setUserDoc] = useState(null);
  const [familyProfiles, setFamilyProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState("");
  const [diversityMode, setDiversityMode] = useState(
    () => localStorage.getItem("diversityMode") === "true"
  );
  const [activeTab, setActiveTab] = useState(0);
  const [generating, setGenerating] = useState(false);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newProfileName, setNewProfileName] = useState("");
  const [userMessage, setUserMessage] = useState(""); // nice inline feedback instead of alerts
    const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editProfileName, setEditProfileName] = useState("");
  const [editIndex, setEditIndex] = useState(null);



  const [buying, setBuying] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [authed, setAuthed] = useState(false);

  const [renewing, setRenewing] = useState(false);

  // Range toggle: 'billing' | 'month' | 'all'
  const [rangeMode, setRangeMode] = useState(() => localStorage.getItem("rangeMode") || "billing");

 // LIVE subscribe to user doc + email verification override
useEffect(() => {
  const unsubAuth = onAuthStateChanged(auth, async (user) => {
    setAuthed(!!user);

    // If no signed-in user AND no overrideUid, we really have nothing to show
    if (!user && !overrideUid) {
      setUserDoc(null);
      setFamilyProfiles([]);
      return;
    }

    // Always ensure the *logged-in* user has a doc (admin or normal user)
    if (user) {
      try {
        await ensureUserDoc();
      } catch {
        // ignore
      }
    }

    // 👇 This is the important part: which user doc are we viewing?
    const targetUid = overrideUid || user?.uid;
    if (!targetUid) {
      setUserDoc(null);
      setFamilyProfiles([]);
      return;
    }

    const ref = doc(db, "users", targetUid);
    const unsubDoc = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setUserDoc({});
          setFamilyProfiles([]);

          // fall back to plain auth verification if we somehow have no doc
          return;
        }

        const d = snap.data() || {};
        setUserDoc(d);



        const profiles = (d.familyProfiles || []).map((p) => ({
          ...p,
          name: p.name || "Reader",
          storiesGenerated: p.storiesGenerated || 0,
          readingActivity: Array.isArray(p.readingActivity) ? p.readingActivity : [],
          readingTime: p.readingTime || [0, 0, 0, 0],
          readingStreak: p.readingStreak || 0,
          favoriteGenres: p.favoriteGenres || {},
        }));
        setFamilyProfiles(profiles);
      },
      (err) => console.error("user snapshot error", err)
    );

    return () => unsubDoc();
  });

  return () => unsubAuth();
}, [auth, db, overrideUid]);




  useEffect(() => {
    const onStorage = () => {
      setDiversityMode(localStorage.getItem("diversityMode") === "true");
      const rm = localStorage.getItem("rangeMode");
      if (rm) setRangeMode(rm);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  /* -------- Derived periods -------- */
  const billingWindow = useMemo(() => getBillingWindow(userDoc || {}), [userDoc]);
  const calendarWindow = useMemo(() => {
    const now = new Date();
    return { start: startOfCalendarMonth(now), end: endOfCalendarMonth(now) };
  }, []);

  function getCurrentWindow() {
    if (rangeMode === "billing") return billingWindow;
    if (rangeMode === "month") return calendarWindow;
    return { start: null, end: null }; // all time
  }

    /* -------- Sidebar usage (annual = monthly view, monthly = billing view) -------- */
  const sidebarUsage = useMemo(() => {
    const cap = resolveCap(userDoc || {});
    const { start: billStart, end: billEnd } = billingWindow;
    const { start: monthStart, end: monthEnd } = calendarWindow;

    const sub = userDoc?.subscription || null;
    const legacyPlan = typeof userDoc?.plan === "object" ? userDoc.plan : null;
    const planString = typeof userDoc?.plan === "string" ? userDoc.plan : null;

    const billingNorm =
      normalizeBilling(sub?.billingInterval) ||
      normalizeBilling(legacyPlan?.billing) ||
      "—";

    // 👇 Annual plans → use calendar month for sidebar usage, monthly → billing window
    const isAnnual = billingNorm === "annual";
    const windowStart = isAnnual ? monthStart : billStart;
    const windowEnd = isAnnual ? monthEnd : billEnd;

    const used = sumStoriesInWindow(familyProfiles, windowStart, windowEnd);
    const addonsRem = remainingAddons(userDoc || {});
    const remainingIncluded = Math.max(cap - used, 0);
    const remainingTotal = remainingIncluded + addonsRem;

    const planLabel =
      sub?.displayName ||
      planString ||
      legacyPlan?.id ||
      "Free";

    const allowedProfiles =
      Number(userDoc?.entitlements?.concurrentUsers || 0) ||
      (sub?.packageKey?.includes("family_lite")
        ? 3
        : sub?.packageKey?.includes("family")
        ? 5
        : sub?.packageKey?.includes("personal")
        ? 1
        : 1);

    // For combined (segmented) bar
    const usedIncluded = Math.min(used, cap);
    const usedFromAddons = Math.max(used - cap, 0);
    const totalPool = cap + addonsRem + usedFromAddons; // cap + remaining add-ons + any add-on already used
    const usedTotal = usedIncluded + usedFromAddons;
    const pctIncluded = totalPool ? (usedIncluded / totalPool) * 100 : 0;
    const pctAddon = totalPool ? (usedFromAddons / totalPool) * 100 : 0;

    return {
      cap,
      used,
      remainingIncluded,
      remainingAddons: addonsRem,
      remainingTotal,
      planLabel,
      billing: billingNorm,
      periodEnd:
        asDateMaybe(sub?.currentPeriodEnd) ||
        asDateMaybe(legacyPlan?.currentPeriodEnd) ||
        null,
      allowedProfiles,
      usedIncluded,
      usedFromAddons,
      totalPool,
      usedTotal,
      pctIncluded,
      pctAddon,
      isAnnual, // 👈 expose to JSX
    };
  }, [userDoc, familyProfiles, billingWindow, calendarWindow]);


  /* -------- Charts & summaries respect the range toggle -------- */
  const currentWindow = getCurrentWindow();
  const { start: rangeStart, end: rangeEnd } = currentWindow;

  // Stories Generated (bar)
  const storiesReadData = useMemo(() => {
    const labels = familyProfiles.map((p) => p.name);
    const data = familyProfiles.map((p) => {
      const arr = Array.isArray(p.readingActivity) ? p.readingActivity : [];
      let total = 0;
      for (const e of arr) {
        const when = e?.date || e?.ts || null;
        if (!rangeStart && !rangeEnd) {
          total += Number(e?.storiesGenerated || 0) || 1;
        } else if (inRange(when, rangeStart, rangeEnd)) {
          total += Number(e?.storiesGenerated || 0) || 1;
        }
      }
      return total;
    });
    return {
      labels,
      datasets: [
        {
          label: "Stories Generated",
          data,
          backgroundColor: "rgba(0, 123, 255, 0.5)",
          borderColor: "rgba(0, 123, 255, 1)",
          borderWidth: 2,
        },
      ],
    };
  }, [familyProfiles, rangeStart, rangeEnd]);

  // Time Spent Reading (line)
  const timeSpentReadingData = useMemo(() => {
    const labels = familyProfiles.map((p) => p.name);
    const data = familyProfiles.map((p) => {
      const arr = Array.isArray(p.readingActivity) ? p.readingActivity : [];
      return arr.reduce((t, e) => {
        const when = e?.date || e?.ts || null;
        if (!rangeStart && !rangeEnd) return t + Number(e?.timeSpent || 0);
        return inRange(when, rangeStart, rangeEnd) ? t + Number(e?.timeSpent || 0) : t;
      }, 0);
    });
    return {
      labels,
      datasets: [
        {
          label: "Total Reading Time (Seconds)",
          data,
          borderColor: "rgba(75, 192, 192, 1)",
          borderWidth: 2,
          fill: false,
        },
      ],
    };
  }, [familyProfiles, rangeStart, rangeEnd]);

  // Reading Streaks (current streaks)
  const readingStreaksData = useMemo(() => {
    const labels = familyProfiles.map((p) => p.name);
    const data = familyProfiles.map((p) => p.readingStreak || 0);
    return {
      labels,
      datasets: [
        {
          label: "Reading Streak (Days)",
          data,
          backgroundColor: "rgba(255, 165, 0, 0.6)",
          borderColor: "rgba(255, 165, 0, 1)",
          borderWidth: 2,
        },
      ],
    };
  }, [familyProfiles]);

  // Favorite Genres (range aware)
  const genreLabels = useMemo(() => {
    const set = new Set();
    for (const p of familyProfiles) {
      const arr = Array.isArray(p.readingActivity) ? p.readingActivity : [];
      for (const e of arr) {
        const when = e?.date || e?.ts || null;
        if (!rangeStart && !rangeEnd) {
          Object.keys(p.favoriteGenres || {}).forEach((g) => set.add(g));
          break;
        } else if (inRange(when, rangeStart, rangeEnd)) {
          Object.keys(p.favoriteGenres || {}).forEach((g) => set.add(g));
          break;
        }
      }
    }
    return Array.from(set);
  }, [familyProfiles, rangeStart, rangeEnd]);

  const favoriteGenresData = useMemo(() => {
    const labels = genreLabels.length ? genreLabels : ["No data yet"];
    const datasets = familyProfiles.map((p, idx) => {
      const arr = Array.isArray(p.readingActivity) ? p.readingActivity : [];
      let include = false;
      if (!rangeStart && !rangeEnd) include = true;
      else include = arr.some((e) => inRange(e?.date || e?.ts || null, rangeStart, rangeEnd));
      const genreMap = include ? (p.favoriteGenres || {}) : {};
      return {
        label: p.name,
        data: labels.map((g) => Number(genreMap[g] || 0)),
        backgroundColor: `rgba(${(idx * 60) % 255}, 99, 255, 0.6)`,
        borderColor: `rgba(${(idx * 60) % 255}, 99, 255, 1)`,
        borderWidth: 2,
      };
    });
    return { labels, datasets };
  }, [genreLabels, familyProfiles, rangeStart, rangeEnd]);

  // Average Reading Time per session (range aware)
  const averageReadingTimeData = useMemo(() => {
    const labels = familyProfiles.map((p) => p.name);
    const data = familyProfiles.map((p) => {
      const arr = Array.isArray(p.readingActivity) ? p.readingActivity : [];
      let tot = 0;
      let cnt = 0;
      for (const e of arr) {
        const when = e?.date || e?.ts || null;
        if (!rangeStart && !rangeEnd) {
          tot += Number(e?.timeSpent || 0);
          cnt += 1;
        } else if (inRange(when, rangeStart, rangeEnd)) {
          tot += Number(e?.timeSpent || 0);
          cnt += 1;
        }
      }
      return cnt ? tot / cnt : 0;
    });
    return {
      labels,
      datasets: [
        {
          label: "Avg. Reading Time (Seconds)",
          data,
          backgroundColor: "rgba(0, 128, 0, 0.6)",
          borderColor: "rgba(0, 128, 0, 1)",
          borderWidth: 2,
        },
      ],
    };
  }, [familyProfiles, rangeStart, rangeEnd]);

  /** Reading Summary (range aware) */
  const summary = useMemo(() => {
    const stories = sumStoriesInWindow(familyProfiles, rangeStart, rangeEnd);
    // total + avg time over range
    let totalTime = 0;
    let sessions = 0;
    for (const p of familyProfiles) {
      const ra = Array.isArray(p.readingActivity) ? p.readingActivity : [];
      for (const e of ra) {
        const when = e?.date || e?.ts || null;
        if (!rangeStart && !rangeEnd) {
          totalTime += Number(e?.timeSpent || 0);
          sessions += 1;
        } else if (inRange(when, rangeStart, rangeEnd)) {
          totalTime += Number(e?.timeSpent || 0);
          sessions += 1;
        }
      }
    }
    let genreCounts = {};
    for (const p of familyProfiles) {
      const ra = Array.isArray(p.readingActivity) ? p.readingActivity : [];
      const hasRangeData =
        !rangeStart && !rangeEnd ? true : ra.some((e) => inRange(e?.date || e?.ts || null, rangeStart, rangeEnd));
      if (hasRangeData) {
        const fav = p.favoriteGenres || {};
        Object.keys(fav).forEach((g) => {
          genreCounts[g] = (genreCounts[g] || 0) + Number(fav[g] || 0);
        });
      }
    }
    let mostPopularGenre = "—";
    let max = -1;
    Object.entries(genreCounts).forEach(([g, n]) => {
      if (n > max) { max = n; mostPopularGenre = g; }
    });

    const averageReadingTimeThisRange = sessions ? Math.round(totalTime / 60 / sessions) : 0;

    return {
      storiesGeneratedThisRange: stories,
      totalReadingTimeThisRange: totalTime,
      averageReadingTimeThisRange,
      mostPopularGenre,
    };
  }, [familyProfiles, rangeStart, rangeEnd]);

  // Comprehension scores over time (range aware)
  const comprehensionScoresData = useMemo(() => {
    const labels = [];
    const data = [];

    familyProfiles.forEach((p) => {
      const arr = Array.isArray(p.readingActivity) ? p.readingActivity : [];
      arr.forEach((e) => {
        const when = e?.date || e?.ts || e?.time || null;
        if (rangeStart || rangeEnd) {
          if (!inRange(when, rangeStart, rangeEnd)) return;
        }

        // Try multiple shapes, same as other plans:
        // 1) direct percentage
        let pct = null;
        if (typeof e.comprehensionScorePercent === "number") {
          pct = e.comprehensionScorePercent;
        } else if (typeof e.comprehensionPercentage === "number") {
          pct = e.comprehensionPercentage;
        }
        // 2) score + total
        else if (
          (typeof e.comprehensionScore === "number" && typeof e.comprehensionTotal === "number") &&
          e.comprehensionTotal > 0
        ) {
          pct = (e.comprehensionScore / e.comprehensionTotal) * 100;
        }
        // 3) nested object: e.comprehension.{correct,total}
        else if (
          e.comprehension &&
          typeof e.comprehension.correct === "number" &&
          typeof e.comprehension.total === "number" &&
          e.comprehension.total > 0
        ) {
          pct = (e.comprehension.correct / e.comprehension.total) * 100;
        }

        if (pct == null) return;

        const labelDate = asDateMaybe(when)?.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        }) || "Session";

        labels.push(`${p.name} – ${labelDate}`);
        data.push(Math.round(pct));
      });
    });

    if (!labels.length) {
      return {
        labels: ["No comprehension data yet"],
        datasets: [
          {
            label: "Comprehension Score (%)",
            data: [0],
            borderColor: "rgba(99, 102, 241, 1)",
            backgroundColor: "rgba(99, 102, 241, 0.4)",
            borderWidth: 2,
          },
        ],
      };
    }

    return {
      labels,
      datasets: [
        {
          label: "Comprehension Score (%)",
          data,
          borderColor: "rgba(99, 102, 241, 1)",
          backgroundColor: "rgba(99, 102, 241, 0.3)",
          borderWidth: 2,
          tension: 0.25,
        },
      ],
    };
  }, [familyProfiles, rangeStart, rangeEnd]);


  /* -------- Actions -------- */
    const handleAddProfile = () => {
    const count = familyProfiles.length;

    // Hit profile limit → show a friendly inline message instead of a browser alert
    if (count >= sidebarUsage.allowedProfiles) {
      setUserMessage(
        `Your plan allows up to ${sidebarUsage.allowedProfiles} profiles. ` +
        `You can manage billing or upgrade your plan if you need more seats.`
      );
      return;
    }

    // Open the dialog for name entry
    setNewProfileName("");
    setUserMessage("");
    setAddDialogOpen(true);
  };

  const handleConfirmAddProfile = async () => {
    const name = (newProfileName || "").trim();
    if (!name) {
      setUserMessage("Please enter a name for the new reader.");
      return;
    }

    const newProf = {
      name,
      storiesGenerated: 0,
      readingActivity: [],
      readingTime: [0, 0, 0, 0],
      readingStreak: 0,
      favoriteGenres: {},
      recentActivity: [],
      sharedStories: [],
    };

    const updated = [...familyProfiles, newProf];
    setFamilyProfiles(updated);

    // ✅ fix bug: use auth.currentUser.uid (not targetUid)
    const targetUid = overrideUid || auth.currentUser?.uid;
    if (targetUid) {
      const ref = doc(db, "users", targetUid);
      setDoc(ref, { familyProfiles: updated }, { merge: true }).catch(() => {});
    }

    setAddDialogOpen(false);
    setNewProfileName("");
    setUserMessage("");
  };

  const handleCancelAddProfile = () => {
    setAddDialogOpen(false);
    setNewProfileName("");
    // don't clear userMessage here – could be showing a plan-limit info message
  };


    const handleEditProfile = (index) => {
    const curr = familyProfiles[index];
    if (!curr) return;

    setEditIndex(index);
    setEditProfileName(curr.name || "");
    setUserMessage("");          // clear any old message
    setEditDialogOpen(true);     // open the dialog
  };

  const handleConfirmEditProfile = async () => {
    const name = (editProfileName || "").trim();
    if (!name) {
      setUserMessage("Please enter a name for the reader.");
      return;
    }

    if (editIndex == null || !familyProfiles[editIndex]) {
      setEditDialogOpen(false);
      return;
    }

    const updated = [...familyProfiles];
    updated[editIndex] = { ...updated[editIndex], name };
    setFamilyProfiles(updated);

    const targetUid = overrideUid || auth.currentUser?.uid;
    if (targetUid) {
      const ref = doc(db, "users", targetUid);
      setDoc(ref, { familyProfiles: updated }, { merge: true }).catch(() => {});
    }

    setEditDialogOpen(false);
    setEditIndex(null);
    setEditProfileName("");
  };

  const handleCancelEditProfile = () => {
    setEditDialogOpen(false);
    setEditIndex(null);
    setEditProfileName("");
  };


  const handleDeleteProfile = (index) => {
    if (!window.confirm("Delete this profile?")) return;
    const updated = familyProfiles.filter((_, i) => i !== index);
    setFamilyProfiles(updated);

    const targetUid = overrideUid || auth.currentUser?.uid;
    if (targetUid) {
      const ref = doc(db, "users", targetUid);
      setDoc(ref, { familyProfiles: updated }, { merge: true }).catch(() => {});
    }
  };

  // Navigate to StoryAppUser (consumption happens there)
const handleGenerateStory = async () => {
 const user = auth.currentUser;
if (!user) {
  alert("Please sign in again.");
  return;
}

  if (!selectedProfile) {
    alert("Please select a profile to generate a story.");
    return;
  }

  if (sidebarUsage.remainingTotal <= 0) {
    alert("You’ve run out of stories for this billing period. Buy an add-on to keep going!");
    return;
  }

  setGenerating(true);
  try {
    navigate(
      `/storyappuser?profile=${encodeURIComponent(
        selectedProfile
      )}&diversity=${diversityMode}&source=family`
    );
  } finally {
    setGenerating(false);
  }
};


  // ✅ Use HTTP twin with Bearer token
  const startAddonCheckout = async (addonKey) => {
    const priceId =
      addonKey === "extra20" ? ADDON_PRICE_IDS.extra20 :
      addonKey === "extra100" ? ADDON_PRICE_IDS.extra100 : "";

    if (!priceId) {
      alert("Add-ons checkout isn’t wired yet. Add your Stripe price IDs in ADDON_PRICE_IDS.");
      return;
    }

    const user = getAuth().currentUser;
    if (!user) {
      alert("Please sign in again.");
      return;
    }

    setBuying(true);
    try {
      await user.getIdToken(true);
      const idToken = await user.getIdToken();

      const resp = await fetch(
        "https://createaddoncheckoutsessionhttp-szcrkw6k7q-uc.a.run.app",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            priceId,
            quantity: 1,
            // Send user straight back here (you can use a dedicated success page if you prefer)
            successUrl: window.location.origin + "/family-dashboard?topup=1",
            cancelUrl:  window.location.origin + "/family-dashboard?cancelled=1",
            metadata: { addonKey },
          }),
        }
      );

      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(json?.error?.message || `Checkout failed (${resp.status}).`);
      }
      if (!json?.url) throw new Error("No checkout URL returned.");
      window.location.href = json.url;
    } catch (err) {
      console.error("Add-on checkout error:", err);
      alert(err?.message || "Could not start checkout.");
    } finally {
      setBuying(false);
    }
  };

  const openPortal = async () => {
    if (!authed || !auth.currentUser) {
      alert("Please sign in again to manage billing.");
      return;
    }
    setOpeningPortal(true);
    try {
      await auth.currentUser.getIdToken(true);
      const t = await auth.currentUser.getIdToken();
      const r = await fetch(
        "https://us-central1-yourstoryworld-df05d.cloudfunctions.net/createPortalSessionHttp",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${t}` },
          body: JSON.stringify({ returnUrl: window.location.origin + "/family-dashboard" }),
        }
      );
      const j = await r.json();
      if (!r.ok) throw new Error(j?.error?.message || `Portal failed: ${r.status}`);
      if (j?.url) window.location.href = j.url;
      else throw new Error("No portal URL returned.");
    } catch (e) {
      console.error(e);
      alert("Could not open billing portal. Please try again in a moment.");
    } finally {
      setOpeningPortal(false);
    }
  };

  const doLogout = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (e) {
      console.error("Logout error:", e);
    }
  };


// Renew an expired subscription by starting a fresh Checkout session
const handleRenewSubscription = async () => {
  if (!authed || !auth.currentUser) {
    alert("Please sign in again to renew your subscription.");
    return;
  }

  // Prefer the priceId stored in the subscription stripe block
  const priceId =
    userDoc?.subscription?.stripe?.priceId ||
    userDoc?.subscription?.priceId ||
    null;

  if (!priceId) {
    // Fallback: send user to the generic subscriptions page
    alert(
      "We couldn't find your previous plan details. " +
      "Please pick your plan again on the subscriptions page."
    );
    localStorage.setItem("selectedPlan", "family");
    navigate("/subscriptions");
    return;
  }

  try {
    setRenewing(true);

    await auth.currentUser.getIdToken(true);
    const idToken = await auth.currentUser.getIdToken();

    const resp = await fetch(
      "https://us-central1-yourstoryworld-df05d.cloudfunctions.net/createSubscriptionCheckoutSessionHttp",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          priceId,
          quantity: 1,
          // Use BillingSuccessRedirect → Family dashboard
          successUrl: `${window.location.origin}/billing-success?to=/family-dashboard`,
          cancelUrl: `${window.location.origin}/subscriptions`,
          metadata: { planKind: "family" },
        }),
      }
    );

    const json = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      throw new Error(json?.error?.message || `Renewal failed (${resp.status}).`);
    }
    if (!json?.url) throw new Error("No checkout URL returned from Stripe.");

    // Help BillingSuccessRedirect pick the right dashboard
    localStorage.setItem("lastDashboard", "/family-dashboard");
    localStorage.setItem("selectedPlan", "family");

    window.location.href = json.url;
  } catch (e) {
    console.error("Renew subscription error:", e);
    alert(e?.message || "Could not start renewal. Please try again.");
  } finally {
    setRenewing(false);
  }
};



  /* -------- UI helpers -------- */
    
  const nearEmpty = sidebarUsage.remainingTotal <= 5;

  const endMs = sidebarUsage.periodEnd ? sidebarUsage.periodEnd.getTime() : null;

// ✅ Expired should flip immediately after period end (no rounding delays)
const isExpired = endMs != null && Date.now() > endMs;

// Days remaining (only for messaging)
const expDays =
  endMs != null ? Math.ceil((endMs - Date.now()) / 86400000) : null;

// 🛟 Belt & braces: billing window must exist
const hasPeriodEnd = !!sidebarUsage.periodEnd;

// 🟡 Renewal soon only when not expired
const nearExpiry = expDays != null && !isExpired && expDays <= 5;



  const userLoading = userDoc === null;
  if (userLoading) return <p style={{ padding: 16 }}>Loading…</p>;

    /* ---------------- Render ---------------- */
  return (
    <div
      className="full-screen-wrapper"
      style={{
        minHeight: "100vh",
        width: "100%",
        overflowY: "auto",
        overflowX: "auto", // allow sideways scroll if content overflows
      }}
    >
      <div
        className="page-container"
        style={{
          backgroundImage: `url(${require("../assets/images/landing_background.jpg")})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: isMobile ? "scroll" : "fixed",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          minHeight: "100vh",
          fontFamily: "Montserrat, sans-serif",
        }}
      >

        {/* Header */}
        <header className="page-header">
          <div className="logo">
            <img
              src={require("../assets/images/logo_name.PNG")}
              alt="YourStoryWorld Logo"
              style={{ height: "50px", cursor: "pointer" }}
              onClick={() => navigate("/")}
            />
          </div>
          <nav style={{ display: "flex", gap: "15px", justifyContent: "flex-end" }}>
            <button onClick={() => navigate("/help")} className="header-button">
              Help
            </button>
            <button onClick={() => navigate("/")} className="header-button">
              Home
            </button>
            <button
              onClick={doLogout}
              className="header-button"
              style={{ backgroundColor: "blue", color: "white", fontWeight: "bold" }}
            >
              Logout
            </button>
          </nav>
        </header>

        {/* Main Content */}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            padding: "20px",
            gap: "20px",
            alignItems: "flex-start",
            flexWrap: "wrap",
            overflowX: "auto",
          }}
        >
          {/* Sidebar */}
          <aside
            style={{
              width: isMobile ? "100%" : 280,
              background: "rgba(230, 245, 255, 0.95)",
              padding: 20,
              borderRadius: 10,
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <div>
              <h2 style={{ fontFamily: "Montserrat, sans-serif", color: "#007BFF", marginBottom: 6 }}>
                {userDoc?.fullName || userDoc?.email || "Family"}
              </h2>

              <div style={{ fontWeight: 700, marginBottom: 8 }}>
                Plan: {sidebarUsage.planLabel} {sidebarUsage.billing !== "—" ? `(${sidebarUsage.billing})` : ""}
              </div>

               <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
  Your plan renews automatically each {sidebarUsage.billing === "annual" ? "year" : "month"} unless you cancel in billing.
</div>

              {/* Combined usage line */}
              <div style={{ fontSize: 12, margin: "8px 0 4px" }}>
  Stories this{" "}
  {sidebarUsage.isAnnual ? "month (from your annual plan)" : "billing period"}:
  {" "}
  <strong>{sidebarUsage.usedTotal}</strong> / {sidebarUsage.totalPool}
</div>


              {/* Combined segmented progress bar */}
              <div
                aria-label="total usage"
                style={{
                  height: 10,
                  background: "#e5e7eb",
                  borderRadius: 999,
                  overflow: "hidden",
                  display: "flex",
                }}
                title={`Included used: ${sidebarUsage.usedIncluded}, Add-on used: ${sidebarUsage.usedFromAddons}`}
              >
                <div style={{ width: `${sidebarUsage.pctIncluded}%`, background: "#3b82f6" }} />
                <div style={{ width: `${sidebarUsage.pctAddon}%`, background: "#10b981" }} />
              </div>

              {/* Legend */}
              <div style={{ fontSize: 11, marginTop: 6, display: "flex", gap: 12, color: "#334155" }}>
                <span>
                  <span style={{ display: "inline-block", width: 10, height: 10, background: "#3b82f6", marginRight: 6 }} />
                  Included
                </span>
                <span>
                  <span style={{ display: "inline-block", width: 10, height: 10, background: "#10b981", marginRight: 6 }} />
                  Add-ons used
                </span>
              </div>

              {/* Balances */}
              <div style={{ fontSize: 12, marginTop: 8 }}>
                Add-ons remaining: <strong>{sidebarUsage.remainingAddons}</strong>
              </div>
              <div style={{ fontSize: 12 }}>
                Total remaining: <strong>{sidebarUsage.remainingTotal}</strong>
              </div>

              <div style={{ fontSize: 12, marginTop: 10 }}>
                Renews: <strong>{formatDate(sidebarUsage.periodEnd)}</strong>
                {nearExpiry && (
                  <div style={{ color: "#b45309", marginTop: 4 }}>
                    ⏳ Renewal soon ({expDays} day{expDays === 1 ? "" : "s"})
                  </div>
                )}
              </div>

              <div style={{ fontSize: 12, marginTop: 10 }}>
                Profiles allowed: <strong>{sidebarUsage.allowedProfiles}</strong>
              </div>
            </div>

            {nearEmpty && (
              <div
                role="alert"
                style={{
                  background: "#fff7ed",
                  border: "1px solid #fed7aa",
                  color: "#9a3412",
                  padding: 10,
                  borderRadius: 8,
                  fontSize: 13,
                }}
              >
                You’re almost out of stories. Consider an add-on to keep going.
              </div>
            )}

            {isExpired && (
  <div
    style={{
      marginTop: 10,
      fontSize: 13,
      color: "#b91c1c",
      background: "#fee2e2",
      border: "1px solid #fecaca",
      padding: "8px 10px",
      borderRadius: 6,
    }}
  >
    <strong>Your subscription has expired.</strong>
    <div style={{ marginTop: 4 }}>
      Please renew or update your plan in the billing portal to keep creating stories.
    </div>
  </div>
)}


            {/* Top-up buttons + helper text */}
            <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
              <button
                className="subscription-button"
                onClick={() => startAddonCheckout("extra20")}
                disabled={buying || !authed}
                title="Buy Extra 20 stories"
              >
                {buying ? "Processing…" : "+ Extra 20"}
              </button>
              <button
                className="subscription-button"
                onClick={() => startAddonCheckout("extra100")}
                disabled={buying || !authed}
                title="Buy Extra 100 stories"
              >
                {buying ? "Processing…" : "+ Extra 100"}
              </button>
            </div>
            <p style={{ fontSize: 12, color: "#334155", marginTop: 6 }}>
              Need more stories this month? Top up your balance instantly. You’ll be redirected to a
              secure Stripe checkout, and your extra stories will be available right away.
            </p>

                        {/* Renew subscription (for expired plans) & Manage billing */}
            <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>
              <button
                onClick={handleRenewSubscription}
                className="subscription-button"
                style={{ width: "100%", opacity: renewing ? 0.8 : 1 }}
                title="Renew your current plan to keep creating stories."
                disabled={!isExpired || renewing || !authed}
              >
                {renewing ? "Starting renewal…" : "Renew Subscription"}
              </button>

              <button
                onClick={openPortal}
                className="subscription-button"
                style={{ width: "100%", opacity: openingPortal ? 0.8 : 1 }}
                title="Change plan, update payment details or cancel your subscription."
                disabled={openingPortal || !authed}
              >
                {openingPortal ? "Opening billing…" : "Change or Cancel Plan / Manage Billing"}
              </button>
            </div>



            {/* Users list */}
            <div style={{ marginTop: 12 }}>
              <h3
                style={{
                  fontFamily: "Montserrat, sans-serif",
                  fontSize: 16,
                  fontWeight: "bold",
                  color: "#007BFF",
                  marginBottom: 10,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Users
              </h3>

                            {userMessage && (
                <p
                  style={{
                    fontSize: 12,
                    color: "#b91c1c",
                    background: "#fee2e2",
                    border: "1px solid #fecaca",
                    padding: "6px 8px",
                    borderRadius: 6,
                    marginBottom: 8,
                  }}
                >
                  {userMessage}
                </p>
              )}


              {familyProfiles.length === 0 ? (
                <p>No users added yet.</p>
              ) : (
                <ul style={{ listStyleType: "none", padding: 0 }}>
                  {familyProfiles.map((p, i) => (
                    <li
                      key={i}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: 8,
                        backgroundColor: "white",
                        borderRadius: 5,
                        marginBottom: 6,
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      }}
                    >
                      <span>{p.name}</span>
                      <div>
                        <button
  onClick={() => handleEditProfile(i)}
  style={{
    marginRight: 6,
    padding: "5px 8px",
    borderRadius: 5,
    backgroundColor: "#ffc107",
    color: "black",
  }}
  title="Edit this user’s name"
>
  ✏️
</button>

                        <button
                          onClick={() => handleDeleteProfile(i)}
                          style={{
                            padding: "5px 8px",
                            borderRadius: 5,
                            backgroundColor: "#dc3545",
                            color: "white",
                          }}
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <button
                onClick={handleAddProfile}
                className="subscription-button"
                style={{ width: "100%", marginTop: 8 }}
              >
                Add User
              </button>
            </div>

            {/* Diversity toggle */}
            <div style={{ marginTop: 12, background: "#f4f4f4", padding: 10, borderRadius: 8 }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  gap: 10,
                  fontSize: 14,
                }}
              >
                <input
                  type="checkbox"
                  checked={diversityMode}
                  onChange={() => {
                    const v = !diversityMode;
                    setDiversityMode(v);
                    localStorage.setItem("diversityMode", String(v));
                  }}
                />
                <span>Enable Diversity Mode</span>
              </label>
              <p style={{ fontSize: 12, color: "#555", marginTop: 6 }}>
                Make characters, settings and themes reflect diverse cultures and backgrounds.
              </p>
            </div>
          </aside>

          {/* Main area */}
<div
  style={{
    flex: 1,
    minWidth: isMobile ? "900px" : 0, // ✅ wider than phone on mobile
  }}
>

            <h1
              style={{ fontFamily: "Montserrat, sans-serif", color: "#007BFF", textAlign: "center" }}
            >
              Welcome to Your Family Dashboard
            </h1>

            {/* Create a Story */}
            <div
              style={{
                background: "rgba(255, 255, 255, 0.9)",
                padding: 30,
                borderRadius: 10,
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                textAlign: "center",
              }}
            >
              <h2>Create a Story</h2>
              <p>Select a profile and click below to generate a story.</p>

              <select
                value={selectedProfile}
                onChange={(e) => setSelectedProfile(e.target.value)}
                style={{
                  padding: 10,
                  marginBottom: 10,
                  borderRadius: 5,
                  border: "1px solid #ccc",
                  width: "100%",
                }}
              >
                <option value="">Select Profile</option>
                {familyProfiles.map((p, i) => (
                  <option key={i} value={p.name}>
                    {p.name}
                  </option>
                ))}
              </select>

              <button
  onClick={handleGenerateStory}
  disabled={
    generating ||
    sidebarUsage.remainingTotal <= 0 ||
    isExpired ||
    !hasPeriodEnd
  }
  style={{
    padding: "15px 30px",
    backgroundColor:
      generating ||
      sidebarUsage.remainingTotal <= 0 ||
      isExpired ||
      !hasPeriodEnd
        ? "#9ca3af"
        : "#007BFF",
    color: "white",
    border: "none",
    borderRadius: 5,
    fontSize: "1rem",
    cursor:
      generating ||
      sidebarUsage.remainingTotal <= 0 ||
      isExpired ||
      !hasPeriodEnd
        ? "not-allowed"
        : "pointer",
    marginTop: 10,
  }}
  title={
  !hasPeriodEnd
    ? "Billing information is still loading. Please refresh in a moment."
    : isExpired
    ? "Your subscription has expired. Renew to keep generating stories."
    : sidebarUsage.remainingTotal <= 0
    ? "No stories left this billing period. Buy an add-on."
    : "Create a Story"
}
>
  {isExpired
  ? "Subscription Expired"
  : generating
  ? "Starting…"
  : "Generate Story"}
</button>



              {sidebarUsage.remainingTotal <= 0 && (
                <div style={{ marginTop: 10, fontSize: 13, color: "#b91c1c" }}>
                  You’re out of stories. Buy an add-on to continue.
                </div>
              )}
            </div>

            {/* Reading Summary (now includes Range buttons) */}
            <div
              className="reading-summary"
              style={{
                background: "linear-gradient(to right, #2C3E50, #4CA1AF)",
                padding: 20,
                borderRadius: 10,
                boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                textAlign: "center",
                marginTop: 20,
                marginBottom: 20,
                color: "white",
              }}
            >
              {/* Range buttons */}
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: "inline-flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    className={`subscription-button ${rangeMode === "billing" ? "active" : ""}`}
                    onClick={() => { setRangeMode("billing"); localStorage.setItem("rangeMode", "billing"); }}
                    title="Show stats for your current subscription billing period."
                    style={{ background: rangeMode === "billing" ? "#0ea5e9" : undefined }}
                  >
                    Billing Period
                  </button>
                  <button
                    className={`subscription-button ${rangeMode === "month" ? "active" : ""}`}
                    onClick={() => { setRangeMode("month"); localStorage.setItem("rangeMode", "month"); }}
                    title="Show stats since the 1st of this calendar month."
                    style={{ background: rangeMode === "month" ? "#0ea5e9" : undefined }}
                  >
                    This Month
                  </button>
                  <button
                    className={`subscription-button ${rangeMode === "all" ? "active" : ""}`}
                    onClick={() => { setRangeMode("all"); localStorage.setItem("rangeMode", "all"); }}
                    title="Show all-time totals."
                    style={{ background: rangeMode === "all" ? "#0ea5e9" : undefined }}
                  >
                    All Time
                  </button>
                </div>
                <div style={{ fontSize: 12, opacity: 0.9, marginTop: 8 }}>
  Charts & summary follow your selection. The <strong>sidebar</strong> shows{" "}
  {sidebarUsage.isAnnual
    ? "your monthly allowance from your annual plan (included cap + add-ons)."
    : "your billing-period balance (included cap + add-ons)."}
</div>

              </div>

              <h2 style={{ marginTop: 6 }}>📊 Reading Summary</h2>
              <p style={{ fontSize: 14, opacity: 0.9 }}>
                Currently showing: <strong>
                  {rangeMode === "billing" ? "Billing Period" : rangeMode === "month" ? "This Month" : "All Time"}
                </strong>
              </p>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-around",
                  marginTop: 10,
                  flexWrap: "wrap",
                  gap: 16,
                }}
              >
                <div>
                  <h3 style={{ color: "#F9C74F", fontSize: "1.8rem" }}>
                    📖 {summary.storiesGeneratedThisRange}
                  </h3>
                  <p>Stories Generated</p>
                </div>
                <div>
                  <h3 style={{ color: "#F94144", fontSize: "1.8rem" }}>
                    ⏳ {Math.round(summary.totalReadingTimeThisRange / 60)} min
                  </h3>
                  <p>Total Reading Time</p>
                </div>
                <div>
                  <h3 style={{ color: "#90BE6D", fontSize: "1.8rem" }}>
                    📊 {summary.averageReadingTimeThisRange} min
                  </h3>
                  <p>Avg. Reading Time</p>
                </div>
                <div>
                  <h3 style={{ color: "#F3722C", fontSize: "1.8rem" }}>
                    🌟 {summary.mostPopularGenre}
                  </h3>
                  <p>Most Popular Genre</p>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div
              style={{
                background: "rgba(255, 255, 255, 0.9)",
                padding: 15,
                borderRadius: 10,
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                marginBottom: 20,
              }}
            >
              <Tabs
                value={activeTab}
                onChange={(e, v) => setActiveTab(v)}
                indicatorColor="primary"
                textColor="primary"
                centered
                sx={{
                  "& .MuiTabs-flexContainer": { justifyContent: "center" },
                  "& .MuiTab-root": {
                    fontWeight: "bold",
                    fontSize: "16px",
                    color: "#333",
                    textTransform: "none",
                  },
                  "& .Mui-selected": { color: "#007BFF !important" },
                }}
              >
                <Tab label="📈 Progress Overview" />
                <Tab label="🔥 Reading Habits" />
                <Tab label="⏳ Reading Insights" />
                <Tab label="🎤 Recordings" />
              </Tabs>
            </div>

            {/* Tab content */}
<div
  style={{
    marginTop: 20,
    paddingBottom: 20,
  }}
>

              {activeTab === 0 && (
  <div
    style={{
      background: "rgba(255, 255, 255, 0.9)",
      padding: 30,
      borderRadius: 10,
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
      marginBottom: 20,
    }}
  >
    <h2>📈 Progress Overview</h2>
    <p style={{ color: "#555", fontSize: 14, marginBottom: 16 }}>
      This section shows <strong>how much each family member reads</strong> and
      <strong> how long they spend reading</strong> in the selected time period.
    </p>

    {/* Stories Generated */}
    <h3 style={{ marginBottom: 6, fontFamily: "Montserrat, sans-serif"
 }}>📖 Stories Generated per Profile</h3>
    <p style={{ color: "#555", fontSize: 13, marginTop: 0, marginBottom: 8 }}>
      Each bar shows how many stories each reader has created in this range.
    </p>
    <Bar
      data={storiesReadData}
      options={{
        responsive: true,
        scales: {
          y: {
            beginAtZero: true,
            title: { display: true, text: "Stories Generated" },
          },
          x: {
            title: { display: true, text: "Family Profiles" },
          },
        },
      }}
    />

    {/* Spacer */}
    <div style={{ height: 28 }} />

    {/* Time Spent Reading */}
    <h3 style={{ marginBottom: 6, fontFamily: "Montserrat, sans-serif"
 }}>⏱ Total Reading Time per Profile</h3>
    <p style={{ color: "#555", fontSize: 13, marginTop: 0, marginBottom: 8 }}>
      This line shows how much total time (in seconds) each reader has spent reading.
    </p>
    <Line
      data={timeSpentReadingData}
      options={{
        responsive: true,
        scales: {
          y: {
            title: { display: true, text: "Total Reading Time (Seconds)" },
            beginAtZero: true,
          },
          x: {
            title: { display: true, text: "Family Profiles" },
          },
        },
      }}
    />
  </div>
)}


              {activeTab === 1 && (
  <div
    style={{
      background: "rgba(255, 255, 255, 0.9)",
      padding: 30,
      borderRadius: 10,
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
      marginBottom: 20,
    }}
  >
    <h2>🔥 Reading Habits</h2>
    <p style={{ color: "#555", fontSize: 14, marginBottom: 16 }}>
      This tab shows <strong>how often your readers come back</strong> and
      <strong> which kinds of stories they choose most</strong>.
    </p>

    {/* Reading Streaks */}
    <h3 style={{ marginBottom: 6, fontFamily: "Montserrat, sans-serif"
 }}>📅 Reading Streaks (Days)</h3>
    <p style={{ color: "#555", fontSize: 13, marginTop: 0, marginBottom: 8 }}>
      Each bar shows the current number of consecutive days each reader has read.
    </p>
    <Bar
      data={readingStreaksData}
      options={{
        responsive: true,
        scales: {
          y: {
            beginAtZero: true,
            title: { display: true, text: "Current Streak (Days)" },
          },
          x: {
            title: { display: true, text: "Family Profiles" },
          },
        },
      }}
    />

    {/* Spacer */}
    <div style={{ height: 28 }} />

    {/* Favorite Genres */}
    <h3 style={{ marginBottom: 6, fontFamily: "Montserrat, sans-serif"
 }}>🌈 Favourite Genres</h3>
    <p style={{ color: "#555", fontSize: 13, marginTop: 0, marginBottom: 8 }}>
      See which genres each reader picks most often. This can guide you to suggest new stories.
    </p>
    <Bar
      data={favoriteGenresData}
      options={{
        responsive: true,
        plugins: {
          legend: { display: true, position: "top" },
          tooltip: { enabled: true },
        },
        scales: {
          x: { title: { display: true, text: "Genres" } },
          y: { title: { display: true, text: "Times Selected" }, beginAtZero: true },
        },
      }}
    />
  </div>
)}


              {activeTab === 2 && (
  <div
    style={{
      background: "rgba(255, 255, 255, 0.9)",
      padding: 30,
      borderRadius: 10,
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    }}
  >
    <h2>📊 Reading Time & Comprehension Insights</h2>
    <p style={{ color: "#555", fontSize: 14, marginBottom: 16 }}>
      This tab shows <strong>how long each reader spends per session</strong>, and
      <strong> how well they answer comprehension questions</strong> after stories.
    </p>

    {/* Average Reading Time */}
    <h3 style={{ marginBottom: 6, fontFamily: "Montserrat, sans-serif"
 }}>⏳ Average Reading Time per Session</h3>
    <p style={{ color: "#555", fontSize: 13, marginTop: 0, marginBottom: 8 }}>
      Each point shows the average time (in seconds) each reader spends per reading session.
    </p>
    <Line
      data={averageReadingTimeData}
      options={{
        responsive: true,
        scales: {
          y: {
            title: { display: true, text: "Avg. Time per Session (Seconds)" },
            beginAtZero: true,
          },
          x: {
            title: { display: true, text: "Family Profiles" },
          },
        },
      }}
    />

    {/* Spacer */}
    <div style={{ height: 28 }} />

    {/* Comprehension Scores */}
    <h3 style={{ marginBottom: 6, fontFamily: "Montserrat, sans-serif"
 }}>🧠 Comprehension Scores</h3>
    <p style={{ color: "#555", fontSize: 13, marginTop: 0, marginBottom: 8 }}>
      Each point represents a quiz score (0–100%) for a story session, grouped by reader and date.
    </p>
    <Line
      data={comprehensionScoresData}
      options={{
        responsive: true,
        scales: {
          y: {
            title: { display: true, text: "Score (%)" },
            beginAtZero: true,
            max: 100,
          },
          x: {
            title: { display: true, text: "Reader & Session" },
          },
        },
        plugins: {
          tooltip: { enabled: true },
          legend: { display: true, position: "top" },
        },
      }}
    />
  </div>
)}



              {activeTab === 3 && (
                <div
                  style={{
                    background: "rgba(255, 255, 255, 0.9)",
                    padding: 30,
                    borderRadius: 10,
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <h2>🎤 Recordings</h2>
                  <p style={{ color: "#555", fontSize: 14, marginBottom: 16 }}>
                    Record read-alouds. You can save up to 3 recordings per user.
                  </p>
                  <VoiceRecorder
                    recorderLabel="Family Read-Aloud Recorder"
                    storyTitle={selectedProfile ? `${selectedProfile} — Latest Story` : "Family Story"}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

                {/* Add User Dialog */}
        <Dialog
          open={addDialogOpen}
          onClose={handleCancelAddProfile}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Add a new reader</DialogTitle>
          <DialogContent>
            <p style={{ fontSize: 14, color: "#555", marginTop: 0 }}>
              Give your new reader a name. You can change this later.
            </p>
            <TextField
              autoFocus
              fullWidth
              margin="dense"
              label="Reader name"
              value={newProfileName}
              onChange={(e) => setNewProfileName(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelAddProfile}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleConfirmAddProfile}
              disabled={!newProfileName.trim()}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

                {/* Edit User Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={handleCancelEditProfile}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Edit reader name</DialogTitle>
          <DialogContent>
            <p style={{ fontSize: 14, color: "#555", marginTop: 0 }}>
              Update this reader’s name. This won’t affect their stories or reading history.
            </p>
            <TextField
              autoFocus
              fullWidth
              margin="dense"
              label="Reader name"
              value={editProfileName}
              onChange={(e) => setEditProfileName(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelEditProfile}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleConfirmEditProfile}
              disabled={!editProfileName.trim()}
            >
              Save changes
            </Button>
          </DialogActions>
        </Dialog>



        {/* Footer */}
        <footer className="page-footer">
          <p>&copy; {new Date().getFullYear()} YourStoryWorld. All rights reserved.</p>
          <nav style={{ marginTop: 10 }}>
            <a href="/about" className="footer-link">
              About Us
            </a>
            <a href="/contact" className="footer-link">
              Contact
            </a>
          </nav>
        </footer>
      </div>
    </div>
  );
};

export default FamilyDashboard;
